import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Crown,
  Settings,
  Plus,
  Shield,
  RefreshCw,
  Search,
  Server,
  Zap,
  Sparkles,
  ChevronRight,
  X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/Skeleton";

type FilterKey = "all" | "active" | "premium" | "setup";

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "premium", label: "Premium" },
  { key: "setup", label: "Needs bot" },
];

export function Dashboard() {
  const { user, isLoggedIn, isDashboardOwner } = useAuth();
  const qc = useQueryClient();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FilterKey>("all");

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["guilds"],
    queryFn: () => api.guilds(),
  });

  const guilds = data?.guilds || [];

  const stats = useMemo(
    () => ({
      total: guilds.length,
      active: guilds.filter((g) => g.bot_in_guild).length,
      premium: guilds.filter((g) => g.is_premium).length,
    }),
    [guilds],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return guilds
      .filter((g) => {
        if (filter === "active") return g.bot_in_guild;
        if (filter === "premium") return g.is_premium;
        if (filter === "setup") return !g.bot_in_guild;
        return true;
      })
      .filter((g) => (q ? g.name.toLowerCase().includes(q) : true))
      .sort((a, b) => {
        if (a.bot_in_guild !== b.bot_in_guild) return a.bot_in_guild ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
  }, [guilds, filter, query]);

  if (error) {
    return (
      <div className="rounded-2xl border border-destructive/40 bg-destructive/5 p-8 text-center">
        <p className="text-destructive">
          {(error as Error).message || "Failed to load servers."}
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl">
      {/* ===== Hero band ===== */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-3xl border border-border/50 bg-gradient-to-br from-primary/10 via-card to-card p-6 sm:p-8"
      >
        <div className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-primary/20 blur-3xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] font-medium text-primary">
              <Sparkles className="h-3 w-3" />
              {user?.username ? `Welcome back, ${user.username}` : "Your workspace"}
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
              Manage your servers
            </h1>
            <p className="text-[13px] text-muted-foreground max-w-md leading-relaxed">
              Pick a server to open its control panel. Only servers where you have{" "}
              <span className="text-foreground/80 font-medium">Manage Server</span> appear here.
            </p>
          </div>

          {/* Stat pills */}
          <div className="flex gap-2.5 shrink-0">
            <StatPill icon={Server} value={stats.total} label="Servers" tint="text-foreground" />
            <StatPill icon={Zap} value={stats.active} label="Active" tint="text-emerald-400" />
            <StatPill icon={Crown} value={stats.premium} label="Premium" tint="text-amber-400" />
          </div>
        </div>
      </motion.div>

      {isDashboardOwner === false &&
        isLoggedIn &&
        user &&
        typeof user.dashboard_owners_total === "number" && (
          <p className="mt-3 px-1 text-xs text-muted-foreground/70">
            The Admin area is decided by the host — ask the service owner if you need that access.
          </p>
        )}

      {/* ===== Toolbar ===== */}
      <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/60" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search servers…"
            className="w-full h-10 rounded-xl border border-border/60 bg-input/40 pl-10 pr-9 text-sm placeholder:text-muted-foreground/60 transition-colors hover:border-border focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary/60 focus-visible:border-primary/60"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Segmented filter */}
        <div className="flex items-center gap-1 rounded-xl border border-border/50 bg-card/50 p-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`relative px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                filter === f.key ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {filter === f.key && (
                <motion.div
                  layoutId="filter-active"
                  className="absolute inset-0 rounded-lg bg-primary"
                  transition={{ type: "spring", stiffness: 500, damping: 35 }}
                />
              )}
              <span className="relative">{f.label}</span>
            </button>
          ))}
        </div>

        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-10 w-10 shrink-0 rounded-xl"
          disabled={isFetching}
          onClick={() => qc.invalidateQueries({ queryKey: ["guilds"] })}
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* ===== List ===== */}
      <div className="mt-4 space-y-2">
        {isLoading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 rounded-2xl border border-border/40 p-3.5">
              <Skeleton className="h-11 w-11 rounded-xl" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="h-9 w-28 rounded-lg" />
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border/50 p-14 text-center">
            <Shield className="h-9 w-9 mx-auto mb-3 text-muted-foreground/50" />
            <h2 className="text-base font-semibold mb-1">
              {query || filter !== "all" ? "No matching servers" : "No accessible servers"}
            </h2>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto">
              {query || filter !== "all"
                ? "Try a different search or filter."
                : "Ask an admin to grant you access or invite the bot to your own server."}
            </p>
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            {filtered.map((g, i) => (
              <motion.div
                key={g.id}
                layout
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.18, delay: Math.min(i * 0.02, 0.2) }}
              >
                <ServerRow g={g} />
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}

function StatPill({
  icon: Icon,
  value,
  label,
  tint,
}: {
  icon: typeof Server;
  value: number;
  label: string;
  tint: string;
}) {
  return (
    <div className="rounded-2xl border border-border/50 bg-background/40 backdrop-blur-sm px-3.5 py-2 text-center min-w-[68px]">
      <div className="flex items-center justify-center gap-1.5">
        <Icon className={`h-3.5 w-3.5 ${tint}`} />
        <span className="text-lg font-semibold tabular-nums">{value}</span>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
        {label}
      </div>
    </div>
  );
}

function ServerRow({
  g,
}: {
  g: {
    id: string;
    name: string;
    icon_url: string | null;
    owner: boolean;
    bot_in_guild: boolean;
    is_premium: boolean;
    invite_url: string;
    can_buy_premium?: boolean;
  };
}) {
  const inner = (
    <div
      className={`group flex items-center gap-4 rounded-2xl border p-3.5 transition-all ${
        g.bot_in_guild
          ? "border-border/50 bg-card/50 hover:bg-card hover:border-primary/30"
          : "border-border/40 bg-card/20 hover:bg-card/40"
      }`}
    >
      {/* Avatar */}
      <div className="relative shrink-0">
        {g.icon_url ? (
          <img src={g.icon_url} alt="" className="h-11 w-11 rounded-xl" />
        ) : (
          <div className="h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center text-lg font-semibold text-primary">
            {g.name[0]?.toUpperCase()}
          </div>
        )}
        {g.bot_in_guild && (
          <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-500 border-2 border-card" />
        )}
      </div>

      {/* Name + meta */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-sm truncate">{g.name}</span>
          {g.owner && <Crown className="h-3.5 w-3.5 text-amber-400 shrink-0" title="Owner" />}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <span className={`text-xs ${g.bot_in_guild ? "text-emerald-400" : "text-muted-foreground"}`}>
            {g.bot_in_guild ? "Bot active" : "Not added"}
          </span>
          {g.is_premium && (
            <span className="inline-flex items-center gap-0.5 text-[11px] text-amber-400 font-medium">
              <Crown className="h-2.5 w-2.5" /> Premium
            </span>
          )}
        </div>
      </div>

      {/* Action */}
      <div className="flex items-center gap-2 shrink-0">
        {g.bot_in_guild ? (
          <>
            {(g.can_buy_premium ?? g.owner) && (
              <Link to={`/premium/${g.id}`} onClick={(e) => e.stopPropagation()}>
                <Button size="icon" variant="ghost" className="h-9 w-9" title="Premium">
                  <Crown className="h-4 w-4 text-amber-400" />
                </Button>
              </Link>
            )}
            <div className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-primary pr-1">
              <Settings className="h-4 w-4" />
              Open
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </div>
            <ChevronRight className="sm:hidden h-5 w-5 text-muted-foreground" />
          </>
        ) : (
          <a href={g.invite_url} onClick={(e) => e.stopPropagation()}>
            <Button size="sm" variant="outline" className="h-9 gap-1.5">
              <Plus className="h-4 w-4" />
              Add bot
            </Button>
          </a>
        )}
      </div>
    </div>
  );

  if (g.bot_in_guild) {
    return (
      <Link to={`/server/${g.id}`} className="block">
        {inner}
      </Link>
    );
  }
  return inner;
}
